import type { Client } from '../../api/client.js';
import { chargerApercuLien } from './apercu-lien.js';
import { envoyerMessage } from './boite.js';
import { controlerModele, type Controle } from './controles-modele.js';
import type {
  ApercuLien,
  CodeModele,
  DossierResume,
  PersonneResume,
  PageArrivee,
  Fil,
  FilDetail,
  ModeleMessage,
  PlanRelance,
  Simulation,
} from './contrat.js';
import { phraseErreur } from './erreurs.js';
import { enregistrerVersion } from './modeles.js';
import { mettreEnPause, reprendre, simuler } from './relances.js';

/** Petit magasin observable, lisible par `useSyncExternalStore`. */
function creerMagasin<E extends object>(initial: E) {
  let etat = initial;
  const abonnes = new Set<() => void>();
  return {
    getEtat: (): E => etat,
    subscribe: (rappel: () => void): (() => void) => {
      abonnes.add(rappel);
      return () => void abonnes.delete(rappel);
    },
    maj: (patch: Partial<E>): void => {
      etat = { ...etat, ...patch };
      abonnes.forEach((a) => a());
    },
  };
}

/** État d'une liste chargée par l'écran : trois cas distincts, jamais confondus. */
export type EtatChargement = 'chargement' | 'pret' | 'erreur';

export interface EtatRattachement {
  dossiers: DossierResume[] | null;
  dossierId: string | null;
  personnes: PersonneResume[] | null;
  recherche: string;
}

export interface EtatBoite {
  liste: EtatChargement;
  rattachement: EtatRattachement | null;
  fils: Fil[] | null;
  ouvert: { fil: Fil; detail: FilDetail } | null;
  texte: string;
  page: PageArrivee;
  apercu: ApercuLien | null;
  retour: string | null;
  envoiEnCours: boolean;
}

export function creerControleurBoite(client: Client) {
  const m = creerMagasin<EtatBoite>({
    liste: 'chargement',
    rattachement: null,
    fils: null,
    ouvert: null,
    texte: '',
    page: 'accueil',
    apercu: null,
    retour: null,
    envoiEnCours: false,
  });
  const majRattachement = (patch: Partial<EtatRattachement>): void => {
    const { rattachement } = m.getEtat();
    if (rattachement) m.maj({ rattachement: { ...rattachement, ...patch } });
  };
  const charger = async (): Promise<void> => {
    try {
      m.maj({ fils: (await client.appeler<{ items: Fil[] }>('messagerie.fils')).items, liste: 'pret', retour: null });
    } catch (e) {
      m.maj({ fils: null, liste: 'erreur', retour: phraseErreur(e) });
    }
  };
  return {
    getEtat: m.getEtat,
    subscribe: m.subscribe,
    charger,
    async ouvrir(fil: Fil): Promise<void> {
      // Un autre fil repart d'une page blanche : ni brouillon, ni rattachement, ni aperçu du fil précédent.
      if (m.getEtat().ouvert?.fil.id !== fil.id) {
        m.maj({ ouvert: null, texte: '', rattachement: null, apercu: null, retour: null, page: 'accueil' });
      }
      try {
        const detail = await client.appeler<FilDetail>('messagerie.fil', { parametres: { filId: fil.id } });
        m.maj({ ouvert: { fil, detail } });
      } catch (e) {
        m.maj({ retour: phraseErreur(e) });
      }
    },
    async reessayer(): Promise<void> {
      m.maj({ liste: 'chargement', retour: null });
      await charger();
    },
    async demarrerRattachement(): Promise<void> {
      m.maj({ rattachement: { dossiers: null, dossierId: null, personnes: null, recherche: '' } });
      try {
        const { items } = await client.appeler<{ items: DossierResume[] }>('dossiers.lister');
        majRattachement({ dossiers: items });
      } catch (e) {
        m.maj({ rattachement: null, retour: phraseErreur(e) });
      }
    },
    async choisirDossier(dossierId: string): Promise<void> {
      majRattachement({ dossierId, personnes: null, recherche: '' });
    },
    async chercherPersonnes(recherche: string): Promise<void> {
      const { rattachement } = m.getEtat();
      if (!rattachement || rattachement.dossierId === null) return;
      majRattachement({ recherche });
      if (recherche.trim().length < 2) {
        majRattachement({ personnes: null });
        return;
      }
      try {
        const { items } = await client.appeler<{ items: PersonneResume[] }>('genealogie.personnes.rechercher', {
          requete: { q: recherche.trim(), dossierId: rattachement.dossierId },
        });
        majRattachement({ personnes: items });
      } catch (e) {
        m.maj({ retour: phraseErreur(e) });
      }
    },
    async rattacher(personId: string | null): Promise<void> {
      const { ouvert, rattachement } = m.getEtat();
      if (!ouvert) return;
      if (!rattachement || rattachement.dossierId === null) {
        m.maj({ retour: 'Choisissez d’abord le dossier auquel rattacher ce fil.' });
        return;
      }
      try {
        const fil = await client.appeler<Fil>('messagerie.rattacher', {
          parametres: { filId: ouvert.fil.id },
          corps: { dossierId: rattachement.dossierId, personId },
        });
        const { fils } = m.getEtat();
        m.maj({
          ouvert: { fil, detail: { ...ouvert.detail, ...fil } },
          fils: fils === null ? fils : fils.map((f) => (f.id === fil.id ? fil : f)),
          rattachement: null,
          retour: 'Le fil est rattaché.',
        });
      } catch (e) {
        m.maj({ retour: phraseErreur(e) });
      }
    },
    annulerRattachement: (): void => m.maj({ rattachement: null }),
    saisir: (texte: string): void => m.maj({ texte }),
    choisirPage: (page: PageArrivee): void => m.maj({ page }),
    async voirLien(): Promise<void> {
      const apercu = await chargerApercuLien(client, m.getEtat().page);
      m.maj(apercu ? { apercu } : { apercu: null, retour: 'L’aperçu du lien est indisponible pour le moment. Réessayez dans un instant.' });
    },
    async envoyer(): Promise<void> {
      const { ouvert, texte, envoiEnCours } = m.getEtat();
      if (!ouvert || envoiEnCours) return;
      m.maj({ envoiEnCours: true });
      try {
        const res = await envoyerMessage(client, ouvert.fil, texte);
        // Le fil a pu changer pendant l'appel : le brouillon du nouveau fil ne doit pas être effacé.
        const memeFil = m.getEtat().ouvert?.fil.id === ouvert.fil.id;
        m.maj(res.ok && memeFil ? { retour: res.message, texte: '' } : { retour: res.message });
      } finally {
        m.maj({ envoiEnCours: false });
      }
    },
  };
}

export interface EtatRelances {
  listePlans: EtatChargement;
  listeModeles: EtatChargement;
  plans: PlanRelance[] | null;
  modeles: ModeleMessage[];
  choisi: ModeleMessage | null;
  corps: string;
  simulation: Simulation | null;
  motif: string;
  retour: string | null;
}

export function creerControleurRelances(client: Client) {
  const m = creerMagasin<EtatRelances>({
    listePlans: 'chargement',
    listeModeles: 'chargement',
    plans: null,
    modeles: [],
    choisi: null,
    corps: '',
    simulation: null,
    motif: '',
    retour: null,
  });
  const chargerPlans = async (): Promise<void> => {
    try {
      m.maj({ plans: await client.appeler<PlanRelance[]>('relances.plans'), listePlans: 'pret' });
    } catch (e) {
      m.maj({ plans: null, listePlans: 'erreur', retour: phraseErreur(e) });
    }
  };
  const chargerModeles = async (): Promise<void> => {
    try {
      m.maj({ modeles: await client.appeler<ModeleMessage[]>('modeles.lister'), listeModeles: 'pret' });
    } catch (e) {
      m.maj({ listeModeles: 'erreur', retour: phraseErreur(e) });
    }
  };
  const charger = async (): Promise<void> => {
    m.maj({ retour: null });
    await Promise.all([chargerPlans(), chargerModeles()]);
  };
  const apresPlan = async (res: { ok: true } | { ok: false; message: string }, succes: string): Promise<void> => {
    m.maj({ retour: res.ok ? succes : res.message });
    if (res.ok) await chargerPlans();
  };
  const controles = (): Controle[] => {
    const { choisi, corps } = m.getEtat();
    return choisi ? controlerModele({ code: choisi.code as CodeModele, corps }) : [];
  };
  return {
    getEtat: m.getEtat,
    subscribe: m.subscribe,
    controles,
    async reessayer(): Promise<void> {
      m.maj({ listePlans: 'chargement', listeModeles: 'chargement' });
      await charger();
    },
    charger,
    saisirMotif: (motif: string): void => m.maj({ motif }),
    pause: async (plan: PlanRelance): Promise<void> =>
      apresPlan(await mettreEnPause(client, plan.id, m.getEtat().motif, null), 'Relances mises en pause.'),
    reprise: async (plan: PlanRelance): Promise<void> => apresPlan(await reprendre(client, plan.id), 'Relances reprises.'),
    async simuler(plan: PlanRelance): Promise<void> {
      if (plan.personId === null) {
        m.maj({ retour: 'Cette relance vise un organisme : la simulation se fait depuis la fiche d’une personne.' });
        return;
      }
      const res = await simuler(client, { dossierId: plan.dossierId, personId: plan.personId, depuis: new Date().toISOString() });
      m.maj(res.ok ? { simulation: res.simulation } : { retour: res.message });
    },
    choisirModele: (choisi: ModeleMessage): void => m.maj({ choisi, corps: choisi.body }),
    modifierCorps: (corps: string): void => m.maj({ corps }),
    async enregistrer(): Promise<void> {
      const { choisi, corps } = m.getEtat();
      if (!choisi) return;
      const res = await enregistrerVersion(client, { code: choisi.code as CodeModele, corps });
      m.maj({ retour: res.ok ? `Version ${res.modele.versionNo} enregistrée en brouillon, à valider par Isabelle.` : res.message });
    },
  };
}
